import React, { useMemo, useState, useEffect } from 'react'

import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { selectData } from 'layouts/onboarding'
import FormField from 'components/FormField'
import ValuePoint from 'admin/components/ValuePoint'
import {
  Card,
  Grid,
  Typography,
  Box,
  Collapse,
  IconButton,
  Snackbar,
} from '@mui/material'
import { ContentCopy, ExpandMore } from '@mui/icons-material'

import { differenceInYears, differenceInMonths, addMonths } from 'date-fns'
import zipState from 'zip-state'

const dateToYears = (startDate, endDate) => {
  const years =
    startDate && endDate
      ? differenceInYears(new Date(endDate), new Date(startDate))
      : ''
  const diff =
    startDate && endDate
      ? differenceInMonths(new Date(endDate), new Date(startDate))
      : ''
  const months =
    startDate && endDate
      ? differenceInMonths(
          new Date(endDate),
          addMonths(new Date(startDate), years * 12)
        )
      : ''
  return { years, months }
}

function BasicInfo({
  collapseSections,
  toggleCollapse,
  intakeState,
  updateIntakeStateAtKey,
  updateIntakeStateAndSubmit,
}) {
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const [localForm, setLocalForm] = useState({
    firstName: intakeState.firstName || '',
    lastName: intakeState.lastName || '',
    phone: intakeState.phone || '',
    dateOfBirth: intakeState.dateOfBirth || '',
    secondaryEmail: intakeState.secondaryEmail || '',
    address: intakeState.address || '',
    address2: intakeState.address2 || '',
    city: intakeState.city || '',
    state: intakeState.state || '',
    zipcode: intakeState.zipcode || '',
  })

  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }

  const stateLogo = useMemo(() => {
    return (
      selectData.states.find(({ value }) => value === intakeState?.state)
        ?.logo || ''
    )
  }, [intakeState?.state])
  const stateFlagStyle = useMemo(
    () => ({
      '& [class$="container"]>div:first-of-type>div:first-of-type>div:first-of-type':
        {
          display: 'flex',
        },
      '& [class$="container"]>div:first-of-type>div:first-of-type>div:first-of-type::before':
        {
          content: '""',
          width: '40px',
          height: '24px',
          marginRight: '8px',
          marginTop: '2px',
          backgroundImage: `url("${stateLogo}")`,
          backgroundSize: 'cover',
        },
    }),
    [stateLogo]
  )

  return (
    <>
      <Grid item xs={12} md={4} xl={3}>
        {/* <Card
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography variant="valueTitle">VALUE POINTS</Typography>
              <Collapse
                in={collapseSections.personal}
                style={{ width: '100%' }}
              ></Collapse>
            </Grid>
          </Grid>
        </Card> */}
      </Grid>
      <Grid item xs={12} md={8} xl={9}>
        <Card
          id="basic-info"
          sx={{
            overflow: 'visible',
            textAlign: 'left',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            borderRadius: 2,
            p: 2,
            pb: 0,
          }}
        >
          <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
            <Grid
              item
              xs={12}
              onClick={() => toggleCollapse()}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="h4" sx={{ flexGrow: 1 }}>
                Personal Information
              </Typography>
              <ExpandMore
                sx={{
                  height: '1.5em',
                  width: '1.5em',
                  transform: collapseSections.personal
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
            </Grid>
          </Grid>
          <Collapse in={collapseSections.personal} style={{ width: '100%' }}>
            <Grid container columnSpacing={3} rowSpacing={2} pb={2}>
              <Grid item xs={12} sm={6} md={3}>
                {renderMuiFormField({
                  id: 'firstName',
                  label: 'First Name',
                  placeholder: 'First Name',
                  inputType: 'text',
                  value: localForm.firstName,
                  onChange: (e) => handleChange('firstName')(e),
                  onBlur: updateIntakeStateAndSubmit((firstName) =>
                    updateIntakeStateAtKey({
                      firstName: firstName,
                      fullName: `${firstName} ${intakeState.lastName}`,
                    })
                  ),
                })}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderMuiFormField({
                  id: 'lastName',
                  label: 'Last Name',
                  placeholder: 'Last Name',
                  inputType: 'text',
                  value: localForm.lastName,
                  onChange: (e) => handleChange('lastName')(e),
                  onBlur: updateIntakeStateAndSubmit((lastName) =>
                    updateIntakeStateAtKey({
                      lastName: lastName,
                      fullName: `${intakeState.firstName} ${lastName}`,
                    })
                  ),
                })}
              </Grid>
              <Grid item xs={8} sm={4} md={3}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'dateOfBirth',
                  label: 'Date of Birth',
                  placeholder: '01/01/1970',
                  inputType: 'date',
                  value: localForm.dateOfBirth,
                  onChange: (e) => handleChange('dateOfBirth')(e),
                  onBlur: updateIntakeStateAndSubmit((dateOfBirth) =>
                    updateIntakeStateAtKey({ dateOfBirth: dateOfBirth })
                  ),
                })}
              </Grid>
              <Grid item xs={4} sm={2} xxl={2}>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  {renderMuiFormField({
                    autoFocus: false,
                    disabled: true,
                    label: 'Age',
                    inputType: 'text',
                    value: dateToYears(intakeState.dateOfBirth, new Date())
                      .years,
                  })}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4} xl={3}>
                <Box display="flex" alignItems="center">
                  <Box flexGrow={1}>
                    {renderMuiFormField({
                      id: 'email',
                      label: 'Email',
                      placeholder: 'Email',
                      inputType: 'text',
                      value: intakeState.email,
                      disabled: true,
                    })}
                  </Box>
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(intakeState.email)
                      setSnackbarOpen(true)
                    }}
                    aria-label="Copy email to clipboard"
                    sx={{
                      ml: 1,
                      borderRadius: '8px',
                      border: '1px solid #ddd',
                      '&:hover': {
                        backgroundColor: '#efefef',
                      },
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                  <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={2000}
                    onClose={() => setSnackbarOpen(false)}
                    message="Email copied to clipboard"
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4} xl={3}>
                {renderMuiFormField({
                  id: 'phone',
                  label: 'Phone Number',
                  placeholder: 'Eg. xxx-xxx-xxxx',
                  inputType: 'tel',
                  value: localForm.phone,
                  onChange: (e) => handleChange('phone')(e),
                  onBlur: updateIntakeStateAndSubmit((phone) =>
                    updateIntakeStateAtKey({ phone: phone })
                  ),
                })}
              </Grid>
              <Grid item xs={12} sm={6} md={4} xl={3}>
                {renderMuiFormField({
                  id: 'secondaryEmail',
                  label: 'Work Email',
                  placeholder: 'name@workplace.com',
                  inputType: 'email',
                  value: localForm.secondaryEmail,
                  onChange: (e) => handleChange('secondaryEmail')(e),
                  onBlur: updateIntakeStateAndSubmit((secondaryEmail) =>
                    updateIntakeStateAtKey({ secondaryEmail })
                  ),
                })}
              </Grid>
              <Grid item xs={12} sm={6} md={4} xl={3}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'gender',
                  label: 'Gender',
                  placeholder: '',
                  inputType: 'select',
                  value: intakeState.gender,
                  onChange: updateIntakeStateAndSubmit((gender) =>
                    updateIntakeStateAtKey({ gender })
                  ),
                  options: selectData.gender,
                })}
              </Grid>
            </Grid>
            <Grid container columnSpacing={3} rowSpacing={2} mt={1} pb={2}>
              <Grid item xs={12} sm={6}>
                {renderMuiFormField({
                  id: 'address',
                  label: 'Street Address',
                  placeholder: '12345 Main Street',
                  inputType: 'text',
                  value: localForm.address,
                  onChange: (e) => handleChange('address')(e),
                  onBlur: updateIntakeStateAndSubmit((address) =>
                    updateIntakeStateAtKey({ address: address })
                  ),
                })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderMuiFormField({
                  id: 'address2',
                  label: 'Apt, Suite, Bldg. (optional)',
                  placeholder: 'Apt, Suite, Bldg. (optional)',
                  inputType: 'text',
                  value: localForm.address2,
                  onChange: (e) => handleChange('address2')(e),
                  onBlur: updateIntakeStateAndSubmit((address2) =>
                    updateIntakeStateAtKey({ address2: address2 })
                  ),
                })}
              </Grid>
              <Grid item xs={12} sm={4}>
                {renderMuiFormField({
                  id: 'city',
                  label: 'City',
                  placeholder: 'City',
                  inputType: 'text',
                  value: localForm.city,
                  onChange: (e) => handleChange('city')(e),
                  onBlur: updateIntakeStateAndSubmit((city) =>
                    updateIntakeStateAtKey({ city: city })
                  ),
                })}
              </Grid>
              <Grid item xs={12} sm={4} sx={stateFlagStyle}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'state',
                  label: 'State',
                  placeholder: 'State',
                  inputType: 'select',
                  value: intakeState.state,
                  onChange: updateIntakeStateAndSubmit((state) =>
                    updateIntakeStateAtKey({ state: state })
                  ),
                  options: selectData.states,
                })}
              </Grid>
              <Grid item xs={12} sm={4}>
                {renderMuiFormField({
                  id: 'zipcode',
                  label: 'Zipcode',
                  placeholder: '#####',
                  inputType: 'zipcode',
                  value: localForm.zipcode,
                  onChange: (e) => handleChange('zipcode')(e),
                  onBlur: updateIntakeStateAndSubmit((zipcode) =>
                    updateIntakeStateAtKey({
                      zipcode: zipcode,
                      state:
                        selectData.states.find(
                          (s) => s.abbr === zipState(zipcode)
                        )?.value || null,
                    })
                  ),
                })}
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Grid>
    </>
  )
}

export default BasicInfo
